/**
 * Footer label for a Vuetify data table: "1-25 din 80", or "0 din 0"
 * when the current page has no visible rows.
 *
 * `visibleCount` is how many rows are actually shown after filters.
 * The page-size window alone is wrong when a filter hides every row
 * (the stock footer still prints "1-25 din 25").
 *
 * @param {{
 *   total?: number,
 *   visibleCount?: number,
 *   page?: number,
 *   itemsPerPage?: number,
 * }} [options]
 * @returns {string}
 */
export function formatDataTablePageText({
    total = 0,
    visibleCount = 0,
    page = 1,
    itemsPerPage = 25,
} = {}) {
    const safeVisible = Math.max(0, Math.floor(Number(visibleCount) || 0));
    if (safeVisible <= 0) {
        return '0 din 0';
    }
    const safeTotal = Math.max(
        safeVisible,
        Math.floor(Number(total) || 0),
    );
    const perPage = Math.max(1, Math.floor(Number(itemsPerPage) || 25));
    const currentPage = Math.max(1, Math.floor(Number(page) || 1));
    const start = (currentPage - 1) * perPage + 1;
    if (start > safeTotal) {
        return '0 din 0';
    }
    const end = Math.min(safeTotal, start + safeVisible - 1);
    return `${start}-${end} din ${safeTotal}`;
}

/**
 * Last valid page for a server-paginated list.
 * An out-of-range request (filter shrank the list while the UI stayed
 * on page 2) must be reloaded, otherwise the table is empty and the
 * footer prints a range past the new total.
 *
 * @param {{ requestedPage?: number, total?: number, pageSize?: number }} [options]
 * @returns {{ page: number, shouldRefetch: boolean }}
 */
/**
 * Footer label for a client-side table whose `items` are the full filtered set.
 * The visible count is the current page slice, so an empty filter reads "0 din 0".
 *
 * @param {number} total
 * @param {number} page
 * @param {number} itemsPerPage
 * @returns {{ page: number, visibleCount: number, text: string }}
 */
export function clientTablePageWindow(total, page, itemsPerPage) {
    const count = Math.max(0, Math.floor(Number(total) || 0));
    const perPage = Math.max(1, Math.floor(Number(itemsPerPage) || 25));
    const pageCount = count > 0 ? Math.max(1, Math.ceil(count / perPage)) : 1;
    const current = Math.min(
        Math.max(1, Math.floor(Number(page) || 1)),
        pageCount,
    );
    const startIndex = (current - 1) * perPage;
    const visibleCount = count <= 0
        ? 0
        : Math.min(perPage, Math.max(0, count - startIndex));
    return {
        page: current,
        visibleCount,
        text: formatDataTablePageText({
            total: count,
            visibleCount,
            page: current,
            itemsPerPage: perPage,
        }),
    };
}

export function resolveServerPage({
    requestedPage = 1,
    total = 0,
    pageSize = 25,
} = {}) {
    const count = Math.max(0, Math.floor(Number(total) || 0));
    const size = Math.max(1, Math.floor(Number(pageSize) || 25));
    const requested = Math.max(1, Math.floor(Number(requestedPage) || 1));
    const lastPage = count > 0 ? Math.max(1, Math.ceil(count / size)) : 1;
    return {
        page: Math.min(requested, lastPage),
        shouldRefetch: requested > lastPage,
    };
}
